import capitalize from 'lodash/capitalize';

import { TYPE_LABELS } from 'lib/constants/transaction/typeLabels';
import { formatDate, formatDateAndTime, mString } from 'lib/helpers/utility';

import {
  APPOINTMENT_RESOURCE_OPTIONS,
  PATIENT_OPTIONS,
  PAYER_OPTIONS,
  PRODUCT_OPTIONS,
  PROVIDER_OPTIONS,
  SERVICE_OPTIONS
} from 'components/shared/Filters/lib/filterOptions';

import PaymentsActions from '../components/PaymentsActions';

const TRANSACTION_TYPES = [
  { label: 'Credit Card', value: 'credit_card' },
  { label: 'Cash', value: 'cash' },
  { label: 'Check', value: 'check' },
  { label: 'Outside Financing', value: 'care_credit' },
  { label: 'Insurance Payments', value: 'era_eob' },
  { label: 'Write-Off', value: 'writeoff' }
];

const STATUS_OPTIONS = [
  { label: 'Completed', value: 'completed' },
  { label: 'Voided', value: 'voided' },
  { label: 'Refunded', value: 'refunded' },
  { label: 'Partially Refunded', value: 'partially_refunded' },
  { label: 'Approval', value: 'APPROVAL' },
  { label: 'Cancelled', value: 'cancelled' },
  { label: 'Pending', value: 'pending' },
  { label: 'Failed', value: 'failed' },
  { label: 'Finalized', value: 'finalized' }
];

const PAYMENT_TYPE_OPTIONS = [
  { label: 'ERA', value: 'era' },
  { label: 'EOB', value: 'eob' },
  { label: 'Patient Payment', value: 'patient_payment' },
  { label: 'Adjustment', value: 'adjustment' }
];

export const INITIAL_FILTERS = {
  date: {
    type: 'date-range',
    title: 'Date of Payment',
    icon: 'calendar-mono'
  },
  type: {
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: PAYMENT_TYPE_OPTIONS.filter((payment_type) =>
          payment_type.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'payment_types.options',
    title: 'Payment Type',
    icon: 'list-unordered-mono'
  },
  appointment_resource: APPOINTMENT_RESOURCE_OPTIONS,
  card_number: {
    title: 'Card Number',
    placeholder: 'Search by last 4 digits',
    icon: 'cards-mono',
    allowed_operators: ['contains', 'does not contain']
  },
  check_number: {
    title: 'Check Number',
    placeholder: 'Search by check number',
    icon: 'check-mono',
    allowed_operators: ['contains', 'does not contain']
  },
  amount: {
    type: 'range',
    title: 'Amount',
    icon: 'dollar-square-mono'
  },
  patient: { ...PATIENT_OPTIONS, allowed_operators: ['is any of', 'is not', 'is'] },
  provider: { ...PROVIDER_OPTIONS, allowed_operators: ['is any of', 'is not', 'is'] },
  payer: { ...PAYER_OPTIONS, allowed_operators: ['is any of', 'is not', 'is'] },
  service: { ...SERVICE_OPTIONS, allowed_operators: ['is any of', 'is not', 'is'] },
  product: { ...PRODUCT_OPTIONS, allowed_operators: ['is any of', 'is not', 'is'] },
  transaction_type: {
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: TRANSACTION_TYPES.filter((transaction_type) =>
          transaction_type.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'transaction_types.options',
    title: 'Transaction Type',
    icon: 'note-2-mono',
    allowed_operators: ['is any of', 'is not', 'is']
  },
  status: {
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: STATUS_OPTIONS.filter((status) =>
          status.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'statuses.options',
    title: 'Status',
    icon: 'status-mono',
    allowed_operators: ['is any of', 'is not', 'is']
  },
  taxable_products: {
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: [
          { label: 'Taxable', value: 'taxable' },
          { label: 'Non-Taxable', value: 'non-taxable' }
        ].filter((status) => status.label.toLowerCase().includes(searchTerm.toLowerCase()))
      };
    },
    queryKey: 'taxable_products.options',
    title: 'Taxable Products',
    icon: 'discount-2-mono',
    allowed_operators: ['is any of', 'is not', 'is']
  }
};

export const COLUMN_DEFS = (practice) => [
  { field: 'id', headerName: 'ID' },
  {
    field: 'type',
    headerName: 'Payment Type',
    valueFormatter: ({ value }) => capitalize(value)
  },
  { field: 'patient', headerName: 'Patient' },
  { field: 'payer', headerName: 'Payer' },

  {
    field: 'created_at',
    headerName: 'Date of Payment',
    valueFormatter: (params) =>
      params?.data?.type === 'Patient Payment'
        ? formatDateAndTime(params?.data?.created_at, practice?.timezone)
        : formatDate(params?.data?.created_at, practice?.timezone)
  },

  { field: 'practitioner', headerName: 'Provider' },
  {
    field: 'check_type',
    headerName: 'Type/Check/Trace',
    valueFormatter: ({ value }) => TYPE_LABELS[value]
  },
  { field: 'cashier', headerName: 'Created by' },
  { field: 'status', headerName: 'Status', valueFormatter: ({ value }) => capitalize(value) },
  {
    field: 'amount',
    headerName: 'Amount',
    valueFormatter: ({ value }) => mString(value)
  },
  { field: 'resource_name', headerName: 'Resource' },
  {
    field: 'actions',
    headerName: 'Actions',
    cellRenderer: PaymentsActions,
    maxWidth: 88,
    minWidth: 36,
    resizable: false,
    sortable: false,
    pinned: 'right'
  }
];
